/**
 * Selection tool of the floorplan persona's Design tab.
 *
 * Design-tab objects are invisible to the global input pipeline's pickable
 * sets, so this tool picks them itself. A click selects, in priority order:
 * sketch-object glyphs, door/window opening sketches (glyph or footprint),
 * beam footprint squares, then room-tag labels; Ctrl/Cmd/Shift accumulates a
 * multi-selection (same semantics as the sketch-object tool). Empty-space
 * clicks stay with the global pipeline (deselect).
 *
 * Manipulation matches the sketch-object tool:
 *  - a single selected opening shows the Side/Hinge double-arrows — click to
 *    flip, drag to move the opening (offset / inset); dragging the opening
 *    BODY slides it along its wall (OpeningFlipGizmoInteraction +
 *    SelectedObjectDragController, shared with the other design tools).
 *  - a single selected sketch object shows the transform gizmo (move /
 *    rotate / scale with undo) plus the run resize handles for LINE runs
 *    (stairs, cabinets, beams); dragging an object's body moves it through
 *    the same placement pipeline (wall alignment, collision).
 */

import * as THREE from 'three';
import { FloorplanToolBase } from './FloorplanToolBase';
import { DragEventData } from '../../BaseTool';
import { Viewer } from '../../../viewer/viewer';
import { IPersona, IViewConfig } from '../../types';
import { ViewModes } from '../../helpers';
import { PixelHitTester } from '../modules/selection/PixelHitTester';
import { OpeningFlipGizmoInteraction } from '../modules/openingGizmos/OpeningFlipGizmos';
import { TransformGizmoInteraction } from '../modules/transformGizmo/TransformGizmoInteraction';
import { SketchObjectGizmoBinding } from '../modules/transformGizmo/SketchObjectGizmoBinding';
import { SelectedObjectDragController } from '../modules/sketchObjects/SelectedObjectDrag';
import { pickOpeningSketchAtPixel } from '../modules/openingSketch/OpeningSketchShared';
import { PointoramaWallSketch } from '../../../utils/PointoramaWallSketch';
import { PointoramaSketchObject, pickSketchObjectAtPixel } from '../../../utils/PointoramaSketchObject';
import { isAnnotationPickBlocked } from '../../../viewer/AnnotationBlacklistStack';
import { projectMouseToFloor } from './WallSketchSnapEngine';

/** Glyph pick radius — matches the sketch-object tool. */
const GLYPH_PICK_RADIUS_PX = 10;
/** Room-label pick radius — matches the room-tag tool. */
const LABEL_PICK_RADIUS_PX = 40;

/** What a mousedown armed for the trailing click (resolved there so a drag —
 *  camera pan or object move — doesn't change the selection). */
type PendingSelect = { kind: 'object'; object: PointoramaSketchObject; additive: boolean } | { kind: 'opening'; openingId: string; additive: boolean } | { kind: 'room_tag'; folderId: string };

export class DesignSelectionTool extends FloorplanToolBase {
  readonly name = 'design_selection';

  /** Tab selection tool: show the floors elevation map for orientation/switching. */
  protected showsFloorBars(): boolean {
    return true;
  }

  private hitTester: PixelHitTester;
  private flipGizmos: OpeningFlipGizmoInteraction;
  private gizmoBinding: SketchObjectGizmoBinding;
  private gizmoInteraction: TransformGizmoInteraction;
  private selectedDrag: SelectedObjectDragController;
  private overlay: THREE.Group | null = null;
  private pendingSelect: PendingSelect | null = null;
  /** Whether onMouseDown blocked the orbit controls for a body/handle drag —
   *  released in onMouseUp (fires on every left release) or onDisable. */
  private dragOrbitBlocked = false;

  private boundRefreshHandles = (): void => this.refreshHandles();
  /** Gizmo/flip interactions are attached only while editing is allowed; detach must mirror that. */
  private interactionsAttached = false;

  /** Read-only viewers select and inspect; nothing moves, flips or resizes. */
  private get canEdit(): boolean {
    return !this.viewer.readOnly;
  }

  constructor(viewer: Viewer, persona: IPersona) {
    super(viewer, persona);
    this.hitTester = new PixelHitTester(viewer);
    // Opt in to undo/redo so gizmo flips/drags are undoable like in the
    // draw and sketch-object tools.
    this.initUndoRedo();
    this.flipGizmos = new OpeningFlipGizmoInteraction({
      viewer,
      hitTester: this.hitTester,
      undoRedo: () => this.undoRedo ?? null,
      getWallSketch: () => this.getWallSketch(),
      manageSelectionSuppression: true,
    });
    this.gizmoBinding = new SketchObjectGizmoBinding(viewer);
    this.gizmoInteraction = new TransformGizmoInteraction({
      viewer,
      undoRedo: () => this.undoRedo ?? null,
      resolveBinding: () => {
        // The gizmo rides the single selected operable sketch object.
        const object = this.singleSelectedObject();
        this.gizmoBinding.setObject(object);
        return object ? this.gizmoBinding : null;
      },
    });
    this.selectedDrag = new SelectedObjectDragController({
      viewer,
      hitTester: this.hitTester,
      getWallSketch: () => this.getWallSketch(),
      getFloorZ: () => this.getFloorZ(),
      undoRedo: () => this.undoRedo ?? null,
      ensureOverlay: () => this.ensureOverlay(),
    });
  }

  getViewConfig(): IViewConfig {
    return {
      views: [ViewModes.XRAY_2D, ViewModes.PLAN_2D_SKELETON, ViewModes.PERSPECTIVE_3D_RGB],
      defaultViewId: ViewModes.XRAY_2D.id,
    };
  }

  protected onEnable(): void {
    super.onEnable();
    if (this.canEdit) {
      this.flipGizmos.attach();
      this.gizmoInteraction.attach();
      this.interactionsAttached = true;
      // Per-frame refresh so the selected run's resize handles appear
      // immediately on select and follow the camera.
      this.viewer.addEventListener('update', this.boundRefreshHandles);
    }
  }

  protected onDisable(): void {
    this.viewer.removeEventListener('update', this.boundRefreshHandles);
    this.releasePendingSelect();
    this.selectedDrag.dispose();
    if (this.interactionsAttached) {
      this.gizmoInteraction.detach();
      this.flipGizmos.detach();
      this.interactionsAttached = false;
    }
    if (this.dragOrbitBlocked) {
      this.dragOrbitBlocked = false;
      this.viewer.inputHandler.unblockOrbitControls();
    }
    if (this.overlay) {
      this.viewer.uiScene.remove(this.overlay);
      this.overlay = null;
    }
    this.viewer.renderer.domElement.style.cursor = 'default';
    super.onDisable();
  }

  dispose(): void {
    this.gizmoInteraction.dispose();
    super.dispose();
  }

  private ensureOverlay(): THREE.Group {
    if (!this.overlay) {
      this.overlay = new THREE.Group();
      this.overlay.name = 'design_selection_overlay';
      this.viewer.uiScene.add(this.overlay);
    }
    return this.overlay;
  }

  private getWallSketch(): PointoramaWallSketch | null {
    if (!this.currentFloorGroupIdentifier) return null;
    return this.viewer.scene.wallSketches.find((s) => s.groupIdentifier === this.currentFloorGroupIdentifier) || null;
  }

  private getFloorZ(): number {
    return this.getWallSketch()?.getFloorHeight() ?? this.currentFloorFloorPlane?.height ?? 0;
  }

  private selectedOperableObjects(): PointoramaSketchObject[] {
    return this.viewer.scene.sketchObjects.filter((o) => o.selected && o.visible && !isAnnotationPickBlocked(this.viewer, o, 'SKETCH_OBJECT'));
  }

  private singleSelectedObject(): PointoramaSketchObject | null {
    const selected = this.selectedOperableObjects();
    return selected.length === 1 && selected[0].getType() ? selected[0] : null;
  }

  /** Per-frame: run resize handles on the single selected LINE run. */
  private refreshHandles(): void {
    if (!this.enabled) return;
    this.selectedDrag.refreshHandles(this.singleSelectedObject());
  }

  // ─── Picking ─────────────────────────────────────────────────────────

  private pickOpeningAtEvent(event: MouseEvent): string | null {
    const skeleton = this.getWallSketch();
    if (!skeleton) return null;
    return pickOpeningSketchAtPixel(this.viewer, skeleton, this.hitTester.mousePx(event), this.hitTester);
  }

  /** A click inside a wall-run object's footprint rectangle picks it (beams
   *  etc. — their glyph is the outline, the interior is easier to hit). */
  private pickBeamByFootprint(event: MouseEvent): PointoramaSketchObject | null {
    const floor = projectMouseToFloor(event, this.viewer.renderer.domElement, this.viewer.scene.getActiveCamera(), this.getFloorZ());
    if (!floor) return null;
    for (const obj of this.viewer.scene.sketchObjects) {
      if (!obj.visible || isAnnotationPickBlocked(this.viewer, obj, 'SKETCH_OBJECT')) continue;
      if (!obj.getType()?.drawLikeWall) continue;
      const frame = obj.resolveFrame();
      if (!frame) continue;
      const dx = floor.x - frame.center.x;
      const dy = floor.y - frame.center.y;
      const u = dx * frame.dir.x + dy * frame.dir.y;
      const v = dx * frame.norm.x + dy * frame.norm.y;
      if (Math.abs(u) <= frame.width / 2 && Math.abs(v) <= frame.depth / 2) return obj;
    }
    return null;
  }

  /** The room label whose anchor projects within pick radius of the cursor
   *  (same projection as the room-tag tool). */
  private pickRoomLabel(event: MouseEvent): string | null {
    const layer = this.viewer.roomLabelsLayer;
    if (!layer) return null;
    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    const camera = this.viewer.scene.getActiveCamera();
    let best: { folderId: string; distance: number } | null = null;
    for (const entry of layer.getLabelEntries()) {
      const projected = entry.position.clone().project(camera);
      if (projected.z > 1) continue; // behind the camera
      const sx = (projected.x * 0.5 + 0.5) * rect.width;
      const sy = (-projected.y * 0.5 + 0.5) * rect.height;
      const distance = Math.hypot(event.clientX - rect.left - sx, event.clientY - rect.top - sy);
      if (distance <= LABEL_PICK_RADIUS_PX && (!best || distance < best.distance)) {
        best = { folderId: entry.folderId, distance };
      }
    }
    return best?.folderId ?? null;
  }

  /** Resolve what a press at this event should select, in the same priority
   *  order as the sketch-object tool: object glyph → opening → beam interior
   *  → room-tag label. */
  private resolvePendingSelect(event: MouseEvent): PendingSelect | null {
    const additive = event.ctrlKey || event.metaKey || event.shiftKey;
    const picked = pickSketchObjectAtPixel(this.viewer, this.hitTester.mousePx(event), this.hitTester, GLYPH_PICK_RADIUS_PX);
    if (picked) return { kind: 'object', object: picked.object, additive };
    const openingId = this.pickOpeningAtEvent(event);
    if (openingId) return { kind: 'opening', openingId, additive };
    const beam = this.pickBeamByFootprint(event);
    if (beam) return { kind: 'object', object: beam, additive };
    const folderId = this.pickRoomLabel(event);
    if (folderId) return { kind: 'room_tag', folderId };
    return null;
  }

  // ─── Mouse choreography ──────────────────────────────────────────────

  onMouseMove(event: MouseEvent): void {
    const domElement = this.viewer.renderer.domElement;
    if (!this.canEdit) {
      domElement.style.cursor = this.resolvePendingSelect(event) ? 'pointer' : 'default';
      return;
    }
    if (this.flipGizmos.isOverHandle(event)) {
      domElement.style.cursor = 'pointer';
      return;
    }
    if (this.gizmoInteraction.handleMouseMove(event)) {
      domElement.style.cursor = 'grab';
      return;
    }
    if (this.selectedDrag.pickHandleAtPixel(this.hitTester.mousePx(event))) {
      domElement.style.cursor = 'ew-resize';
      return;
    }
    domElement.style.cursor = this.resolvePendingSelect(event) ? 'pointer' : 'default';
  }

  onMouseDown(event: MouseEvent): void {
    if (this.canEdit && this.flipGizmos.handleMouseDown(event)) return;
    if (this.canEdit && this.gizmoInteraction.handleMouseDown(event)) return;
    this.selectedDrag.cancel();
    this.releasePendingSelect();
    if (event.button !== 0) return;

    // A resize handle beats grabbing the object under it.
    if (this.canEdit && this.selectedDrag.armResizeHandle(event)) {
      this.blockOrbit();
      return;
    }

    const pending = this.resolvePendingSelect(event);
    if (!pending) return;
    this.pendingSelect = pending;
    // Design-tab objects are invisible to the global pick, so the click's
    // parallel selection pass would land on empty space and DESELECT right
    // after we select. Suppress it for this press; restored in onClick /
    // onDragStart / onDisable.
    this.viewer.inputHandler.selectionDisabled = true;

    // Arm the matching body drag alongside the select: a plain click
    // selects, a real drag moves — same dual role as the sketch tool.
    if (!this.canEdit) return;
    if (pending.kind === 'object') {
      const type = pending.object.getType();
      if (type?.drawLikeWall) this.selectedDrag.armBeamBodyDrag(pending.object, event);
      else if (type) this.selectedDrag.armObjectBodyDrag(pending.object, type, event);
    } else if (pending.kind === 'opening') {
      this.selectedDrag.armOpeningSlide(event);
    }
    if (this.selectedDrag.isArmed()) this.blockOrbit();
  }

  onMouseUp(event: MouseEvent): void {
    if (event.button !== 0) return;
    if (this.dragOrbitBlocked) {
      this.dragOrbitBlocked = false;
      this.viewer.inputHandler.unblockOrbitControls();
    }
  }

  onDragStart(data: DragEventData): void {
    if (this.canEdit && this.flipGizmos.handleDragStart()) return;
    if (this.canEdit && this.gizmoInteraction.handleDragStart(data)) return;
    // A drag is a move (or camera gesture), never a select.
    this.releasePendingSelect();
  }

  onDrag(data: DragEventData): void {
    if (!this.canEdit) return;
    if (this.flipGizmos.handleDrag(data.event)) return;
    if (this.gizmoInteraction.handleDrag(data)) return;
    this.selectedDrag.handleDrag(data);
  }

  onDragEnd(data: DragEventData): void {
    if (this.canEdit && this.flipGizmos.handleDragEnd()) return;
    if (this.canEdit && this.gizmoInteraction.handleDragEnd(data)) return;
    if (this.canEdit && this.selectedDrag.handleDragEnd()) return;
    this.releasePendingSelect();
  }

  onClick(event: MouseEvent): void {
    if (this.canEdit && this.flipGizmos.handleClick()) return;
    if (this.canEdit && this.gizmoInteraction.handleClick(event)) return;
    // Trailing click of a moved drag / sub-threshold click on a handle.
    if (this.canEdit && this.selectedDrag.handleClick(event)) {
      this.releasePendingSelect();
      return;
    }
    const pending = this.pendingSelect;
    if (!pending) return;
    this.releasePendingSelect();
    switch (pending.kind) {
      case 'object':
        this.viewer.scene.dispatchEvent({ type: 'measurement_select', value: { measure: pending.object, isCtrl: pending.additive } });
        return;
      case 'opening': {
        const openingSketch = this.viewer.scene.openingSketches.find((o) => o.identifier === pending.openingId);
        if (!openingSketch) return;
        this.viewer.scene.dispatchEvent({ type: 'measurement_select', value: { measure: openingSketch, isCtrl: pending.additive } });
        return;
      }
      case 'room_tag':
        this.viewer.scene.dispatchEvent({ type: 'room_tag_select', value: { folderId: pending.folderId } });
        return;
    }
  }

  private blockOrbit(): void {
    // Block RIGHT NOW rather than at the drag threshold: waiting loses the
    // race against the camera controls (same reasoning as the sketch tool).
    if (this.dragOrbitBlocked) return;
    this.dragOrbitBlocked = true;
    this.viewer.inputHandler.blockOrbitControls();
  }

  private releasePendingSelect(): void {
    if (!this.pendingSelect) return;
    this.pendingSelect = null;
    this.viewer.inputHandler.selectionDisabled = false;
  }
}
